"use client";

import { useCallback, useState } from "react";
import type { CrossSellOfferId, CrossSellPlacement } from "@/libs/cross-sell-v2";
import { trackCrossSellClick, trackCrossSellTeaseView } from "@/hooks/useCrossSellTracking";

/**
 * Click handler for cross-sell CTAs.
 *
 * When `ctaUrl` is set, callers should render a real
 * `<a href={ctaUrl} target="_blank" rel="noopener noreferrer">` and use
 * `handleClick` only for analytics — native anchor navigation is what Safari
 * and popup blockers allow. Do not use window.open here.
 */
export function useCrossSellClickAction(
  offerId: CrossSellOfferId,
  placement: CrossSellPlacement,
  ctaUrl?: string,
) {
  const [teaseOpen, setTeaseOpen] = useState(false);

  const handleClick = useCallback(() => {
    trackCrossSellClick(offerId, placement);
    if (ctaUrl) return;
    trackCrossSellTeaseView(offerId, placement);
    setTeaseOpen(true);
  }, [offerId, placement, ctaUrl]);

  const closeTease = useCallback(() => {
    setTeaseOpen(false);
  }, []);

  return { teaseOpen, handleClick, closeTease, ctaUrl };
}
